import React, {FC, useMemo, useState} from "react";
import {Paragraph} from "../../../lib/src/components/Paragraph";
import { Input } from "@jane/lib/src/components/form/Input";
import {JaneEmployee} from "../resources/EmployeeContext";
import {Select} from "../../../lib/src/components/form/Select";
import {formatCallPhoneNumber} from "../telephone/formatCallPhoneNumber";
import {Call} from "../telephone/Call";
import {
  Client,
  ClientContact,
  ClientContactDetails,
  MatchedItem,
  Team,
  useApiCall,
  useApiEndpoint
} from "../../../lib/src/api";
import {config} from "../config";
import {useToasts} from "../../../lib/src/toasts/toasts";
import {BuddyTeam, PhoneDirectoryEntry} from "../../../lib/src/api/repositories/buddy-teams-repository";
import {useKeycloak} from "@react-keycloak/web";
import {ModalFooter, useModalControls} from "@jane/lib/src/components/layout/ModalProvider";

export const AddCallbackRequestModal: FC<{cachedMatch: MatchedItem|undefined, clients: Client[], clientContacts: ClientContactDetails[], employees: JaneEmployee[], call: Call, teams: Team[], buddyTeams: BuddyTeam[], directory: PhoneDirectoryEntry[]}> = (props) => {
  const coordinators = props.employees.filter(e => e.role === "Coordinator")
  const [message, setMessage] = useState(generateDefaultMessage(props.cachedMatch, props.call, props.clients, props.employees))
  const {close} = useModalControls()
  const {showToast} = useToasts()
  const {telephone} = useApiCall(config)
  const {keycloak} = useKeycloak()
  const [selectedBuddyTeam,setSelectedBuddyTeam] = useState(props.cachedMatch?.buddy_team_id?.toString() ?? "-")
  const [selectedTeamString, setSelectedTeamString] = useState<string>('(team)')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const { resource: templates, isLoading, reload} = useApiEndpoint(() => telephone.browseCallbackTemplates())
  const callbackTemplateOptions = templates ?
    Object.fromEntries([
      ["-", " -- Selecteer een template -- "],
      ...templates.map((template) => [template.id, template.name]),
    ]) : [];
  const enrichedBuddyTeams = useMemo(() => {
    return props.buddyTeams.map(buddyTeam => {
      const teams = props.teams.filter(t => buddyTeam.ons_teams.some(bt => bt.ons_team_id === t.id))
      const directory = props.directory.find(d => d.extension === buddyTeam.target)
      return teams.map(team => ({
        ...buddyTeam,
        team,
        directory
      }))
    }).flat().sort((a, b) => {
      const testA = a.team?.name !== undefined ? a.team.name : Infinity
      const testB = b.team?.name !== undefined ? b.team?.name : Infinity
      if (testA === testB) return 0;
      return testA > testB ? 1 : -1;
    })
  }, [props.teams, props.buddyTeams, props.directory])
  const selectOptions = useMemo(() => {
    return Object.fromEntries([
      ["-", " -- Selecteer een team -- "],
      ...enrichedBuddyTeams.map(buddyTeam => [buddyTeam.id.toString(), `${buddyTeam.team?.name}`]),
    ])
  }, [enrichedBuddyTeams])

  const submit = async () => {
    await telephone.addCallbackRequest(selectedBuddyTeam, message).then(() => {
      showToast('Aangemaakt', 'Terugbelverzoek aangemaakt')
    }).catch(() => {
      showToast('Oeps', 'Er is iets misgegaan bij het aanmaken van het terugbelverzoek')
    })
  }
  if (coordinators.length === 0) {
    return <div className={"flex flex-col space-y-3"}>
      <Paragraph>Er zijn geen collega's beschikbaar om een terugbelverzoek aan te maken</Paragraph>
      <ModalFooter text={'Sluiten'} onSubmit={close} />
    </div>
  }
  return <div className={"flex flex-col space-y-3"}>
    <Paragraph>Je staat op het punt om een gesprek door te sturen. Vul hieronder de extensie van de collega in waarnaar je het gesprek wilt doorsturen</Paragraph>
    <Select label={'Terugbel sjabloon'} options={callbackTemplateOptions} value={selectedTemplate} onChange={(temp) => {
      setSelectedTemplate(temp)
      const template = templates?.find((t) => t.id === temp)
      if (template) {
        const selectedBuddyTeamData = enrichedBuddyTeams.find(bt => bt.id === selectedBuddyTeam)
        const team = selectedBuddyTeamData?.team;
        const text = template.text
          .replaceAll('(telefoon)', generateDefaultMessage(props.cachedMatch, props.call, props.clients, props.employees))
          .replaceAll('(medewerker)', keycloak.tokenParsed?.name ?? 'Onbekend')
          .replaceAll('(client)', props.cachedMatch?.type === 'client' ?
            props.clients.find((c) => c.id === props.cachedMatch?.id)?.name ?? 'Onbekend'
            : props.clientContacts.find((cc) => cc.id === props.cachedMatch?.id)?.name ?? 'Onbekend')
          .replaceAll('(clientnummer)', props.cachedMatch?.type === 'client'
            ? props.clients.find((c) => c.id === props.cachedMatch?.id)?.client_number ?? 'Onbekend'
            : '')
          .replaceAll('(team)', team ? team.name : '(team)')
        setMessage(text)
      }
    }}/>
    <Select label={'Coördinator'} options={selectOptions} value={selectedBuddyTeam} onChange={(bt) => {
        setSelectedBuddyTeam(bt);
        const selectedBuddyTeamData = enrichedBuddyTeams.find(buddyTeam => buddyTeam.id === bt)
        if (selectedBuddyTeamData?.team) {
          const text = message.replaceAll(selectedTeamString, selectedBuddyTeamData.team.name ?? '(team)');
          setMessage(text);
          setSelectedTeamString(selectedBuddyTeamData.team.name ?? '(team)')
        }
      }}
    />
    <Input type={'textarea'} label={'Bericht'} height={'64'} value={message} focus={true} onChange={setMessage} maxWidth={"full"} />
    <ModalFooter text={'Terugbelverzoek aanmaken'} isDisabled={selectedBuddyTeam === '-'} onSubmit={async () => {
      await submit()
      close()
    }} />
  </div>
}

function generateDefaultMessage(match: MatchedItem|undefined, call: Call, clients: Client[], employees: JaneEmployee[]) {
  if (match?.type === 'client') {
    const client = clients.find(c => c.id === match.id)
    if (client === undefined) {
      return `Cliënt met nummer (${formatCallPhoneNumber(call)}) heeft gebeld.\n\n`
    }
    return `${client.name} (${formatCallPhoneNumber(call)}) heeft gebeld.\n\n`
  }
  if (match?.type === 'employee') {
    const employee = employees.find(e => e.id === match.id)
    if (employee === undefined) {
      return `Medewerker met nummer (${formatCallPhoneNumber(call)}) heeft gebeld.\n\n`
    }
    return `${employee.name} (${formatCallPhoneNumber(call)}) heeft gebeld.\n\n`
  }
  if (match?.type === 'client_contact') {
    const client = clients.find(c => c.id === match.client_id)
    if (client === undefined) {
      return `Contactpersoon van een cliënt heeft gebeld met nummer: (${formatCallPhoneNumber(call)}).\n\n`
    }
    return `Contactpersoon van cliënt: ${client.name} (#${client.client_number}) heeft gebeld met nummer: (${formatCallPhoneNumber(call)}).\n\n`
  }
  // Fallback
  return `${formatCallPhoneNumber(call)} heeft gebeld.\n\n`
}